import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import type { FindPendingShippingResponse } from '../models/routing.types';
import {
  ShippingsForRoutingResponse,
  ShippingForRouting,
  ZonesForRoutingResponse,
  RouteGenerateResponse,
  CreateRouteRequest,
  CreateRouteResponse,
  GenerateRouteRequest,
  Zone,
  RemoveShippingsFromRouteResponse,
  ResolveRouteOperatorResponse,
} from '../models/routing.types';
import { mapFindPendingToShippingForRouting } from '../utils/routingMappers';

const routingService = createApi({
  reducerPath: 'routingService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['Routing', 'RoutingZones', 'Route', 'Routes'] as const,
  endpoints: (builder) => ({
    getShippingsForRouting: builder.mutation<
      ShippingsForRoutingResponse,
      { numbers: number[]; logisticOperatorId: string }
    >({
      query: ({ numbers, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        const validNumbers = Array.isArray(numbers)
          ? numbers
              .map((n) => {
                const num = typeof n === 'string' ? Number(n) : n;
                return isNaN(num) ? null : num;
              })
              .filter((n): n is number => n !== null && n > 0)
          : [];

        return {
          url: '/shipping/for-routing',
          method: 'POST',
          body: {
            numbers: validNumbers,
            logisticOperatorId: trimmedOperatorId,
          },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing'],
    }),

    getShippingByNumberForRouting: builder.query<
      ShippingForRouting,
      { number: number; logisticOperatorId: string }
    >({
      query: ({ number, logisticOperatorId }) => ({
        url: `/shipping/${number}`,
        headers: {
          'x-logistic-operator-id': logisticOperatorId,
        },
      }),
      providesTags: (_result, _error, { number }) => [
        { type: 'Routing' as const, id: number },
        'Routing',
      ],
    }),

    getZonesForRouting: builder.query<
      ZonesForRoutingResponse,
      { logisticOperatorId: string }
    >({
      query: ({ logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';

        return {
          url: '/zones/for-routing',
          params: { logisticOperatorId: trimmedOperatorId },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      providesTags: ['RoutingZones'],
    }),

    generateRoute: builder.mutation<
      RouteGenerateResponse,
      GenerateRouteRequest
    >({
      query: (requestBody) => {
        const logisticOperatorId =
          requestBody.routeLogisticOperatorId?.trim() ||
          requestBody.shippings?.[0]?.logisticOperatorId ||
          '';

        return {
          url: '/routes/generate',
          method: 'POST',
          body: { ...requestBody, routeLogisticOperatorId: logisticOperatorId },
          headers: {
            'x-logistic-operator-id': logisticOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing'],
    }),

    resolveRouteOperator: builder.mutation<
      ResolveRouteOperatorResponse,
      { shippingNumbers: number[] }
    >({
      query: ({ shippingNumbers }) => ({
        url: '/routes/resolve-operator',
        method: 'POST',
        body: { shippingNumbers },
      }),
    }),

    enrichCustomRoute: builder.mutation<
      RouteGenerateResponse,
      {
        points: Array<{
          lat: number;
          long: number;
          tag?: string;
          precedenceGroupId?: string;
          precedenceOrder?: number;
        }>;
      }
    >({
      query: (body) => ({
        url: '/routes/enrich-custom-route',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Routing'],
    }),

    updateVisitOrder: builder.mutation<
      void,
      { routeId: string; visitIds: string[] }
    >({
      query: ({ routeId, visitIds }) => ({
        url: `/routes/${routeId}/visits/order`,
        method: 'PUT',
        body: { visitIds },
      }),
      invalidatesTags: ['Route', 'Routes'],
    }),

    updateVisitCoordinates: builder.mutation<
      void,
      {
        routeId: string;
        visitId: string;
        coordinates: {
          lat: number;
          long: number;
          coordinatesChanged: boolean;
          changedByUserId?: string;
        };
      }
    >({
      query: ({ routeId, visitId, coordinates }) => ({
        url: `/routes/${routeId}/visit/${visitId}/coordinates`,
        method: 'PUT',
        body: coordinates,
      }),
      invalidatesTags: ['Route', 'Routes'],
    }),

    createRoute: builder.mutation<
      CreateRouteResponse,
      {
        routeData: CreateRouteRequest;
        logisticOperatorId: string;
      }
    >({
      query: ({ routeData, logisticOperatorId }) => ({
        url: '/routes',
        method: 'POST',
        body: routeData,
        headers: {
          'x-logistic-operator-id': logisticOperatorId,
        },
      }),
      invalidatesTags: ['Route', 'Routes', 'Routing'],
    }),

    updateRoute: builder.mutation<
      CreateRouteResponse,
      {
        routeId: string;
        routeData: Omit<CreateRouteRequest, 'id'>;
        logisticOperatorId: string;
      }
    >({
      query: ({ routeId, routeData, logisticOperatorId }) => ({
        url: `/routes/${routeId}`,
        method: 'PUT',
        body: routeData,
        headers: {
          'x-logistic-operator-id': logisticOperatorId,
        },
      }),
      invalidatesTags: ['Route', 'Routes', 'Routing'],
    }),

    searchZones: builder.query<
      PaginatedResponse<Zone>,
      {
        logisticOperatorId: string;
        search?: string;
        paginated: PaginatedRequest;
      }
    >({
      query: ({ logisticOperatorId, search, paginated }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        const params: any = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          logisticOperatorId: trimmedOperatorId,
        };

        if (search && search.trim() !== '') {
          params.search = search.trim();
        }

        return {
          url: '/zones',
          params,
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      providesTags: ['Routing'],
    }),

    createPolygon: builder.mutation<
      Zone,
      {
        name: string;
        geojson: any;
        logisticOperatorId: string;
        color?: string;
      }
    >({
      query: ({ name, geojson, logisticOperatorId, color }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        return {
          url: '/zones/create-polygon',
          method: 'POST',
          body: {
            name: name.trim(),
            geojson,
            logisticOperatorId: trimmedOperatorId,
            ...(color && { color }),
          },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing'],
    }),

    updateZone: builder.mutation<
      Zone,
      {
        zoneId: string;
        geojson: any;
        logisticOperatorId: string;
      }
    >({
      query: ({ zoneId, geojson, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        return {
          url: `/zone/${zoneId}`,
          method: 'PUT',
          body: {
            geojson,
            logisticOperatorId: trimmedOperatorId,
          },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing'],
    }),

    deleteZone: builder.mutation<
      void,
      { zoneId: string; logisticOperatorId: string }
    >({
      query: ({ zoneId, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        return {
          url: `/zone/${zoneId}`,
          method: 'DELETE',
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing'],
    }),

    findShippingByNumber: builder.mutation<
      ShippingForRouting,
      {
        search: string;
        logisticOperatorId: string;
      }
    >({
      query: ({ search, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        const trimmedSearch = (search ?? '').toString().trim();
        const parsedNumber = Number(trimmedSearch);
        const numberFromSearch =
          !Number.isNaN(parsedNumber) && parsedNumber > 0
            ? parsedNumber
            : undefined;
        return {
          url: '/shipping/find-by-number-for-routing',
          method: 'POST',
          body: {
            search: trimmedSearch,
            logisticOperatorId: trimmedOperatorId,
            ...(numberFromSearch !== undefined
              ? { number: numberFromSearch }
              : {}),
          },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      transformResponse: (
        response: ShippingForRouting | ShippingsForRoutingResponse
      ): ShippingForRouting => {
        if (
          response &&
          typeof response === 'object' &&
          'shippings' in response
        ) {
          const arr = (response as ShippingsForRoutingResponse).shippings;
          if (Array.isArray(arr) && arr.length > 0) return arr[0];
          throw new Error('No se encontró un pedido con ese número');
        }
        return response as ShippingForRouting;
      },
      invalidatesTags: ['Routing'],
    }),

    findPendingShipping: builder.mutation<
      ShippingForRouting,
      { code: string; logisticOperatorId: string }
    >({
      query: ({ code, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        const trimmedCode = (code ?? '').toString().trim();
        return {
          url: `/shipping/find-pending/${encodeURIComponent(trimmedCode)}`,
          method: 'GET',
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      transformResponse: (
        response: FindPendingShippingResponse,
        _meta,
        arg: { code: string; logisticOperatorId: string }
      ) => mapFindPendingToShippingForRouting(response, arg.logisticOperatorId),
      invalidatesTags: ['Routing'],
    }),

    findShippingsByPolygon: builder.mutation<
      ShippingsForRoutingResponse,
      {
        polygon: { type: 'Polygon'; coordinates: number[][][] };
        logisticOperatorId: string;
      }
    >({
      query: ({ polygon, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        return {
          url: '/shipping/find-by-polygon',
          method: 'POST',
          body: {
            polygon,
            logisticOperatorId: trimmedOperatorId,
          },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
    }),

    removeShippingsFromRoute: builder.mutation<
      RemoveShippingsFromRouteResponse,
      { routeId: string; shippingNumbers: number[]; logisticOperatorId: string }
    >({
      query: ({ routeId, shippingNumbers, logisticOperatorId }) => {
        const trimmedOperatorId = logisticOperatorId?.trim() || '';
        return {
          url: `/routes/${routeId}/remove-shippings`,
          method: 'POST',
          body: { shippingNumbers },
          headers: {
            'x-logistic-operator-id': trimmedOperatorId,
          },
        };
      },
      invalidatesTags: ['Routing', 'Route', 'Routes'],
    }),
  }),
});

export default routingService;

export const {
  useGetShippingsForRoutingMutation,
  useGetShippingByNumberForRoutingQuery,
  useGetZonesForRoutingQuery,
  useGenerateRouteMutation,
  useResolveRouteOperatorMutation,
  useEnrichCustomRouteMutation,
  useUpdateVisitOrderMutation,
  useUpdateVisitCoordinatesMutation,
  useCreateRouteMutation,
  useUpdateRouteMutation,
  useSearchZonesQuery,
  useCreatePolygonMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useFindShippingByNumberMutation,
  useFindPendingShippingMutation,
  useFindShippingsByPolygonMutation,
  useRemoveShippingsFromRouteMutation,
} = routingService;
